import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money, dims } from "@/lib/api";

const draft = (p) =>
  [
    `A new original painting, "${p.title}", has just been finished in the studio.`,
    [p.medium, dims(p), money(p.price)].filter(Boolean).join(" · "),
    "Because you're on the waitlist, you're hearing about it before anyone else. Each original is one of a kind, so if it speaks to you, have a look soon.",
    "Warmly,\nLaszlo",
  ].join("\n\n");

export const TellWaitlistDialog = ({ product, onClose }) => {
  const [count, setCount] = useState(null);
  const [f, setF] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!product) return;
    setF({ subject: `A new original: ${product.title}`, message: draft(product) });
    setCount(null);
    api.get("/admin/waitlist").then((r) => setCount(new Set(r.data.map((i) => i.email)).size)).catch(() => setCount(0));
  }, [product]);

  const send = async () => {
    if (!window.confirm(`Email ${count} ${count === 1 ? "person" : "people"} on your waitlist about "${product.title}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/email", { ...f, productId: product.id, artworkSlug: null });
      toast.success(`Waitlist told — sent to ${data.sent}${data.failed ? ` · ${data.failed} failed` : ""}`);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-none bg-[#FDFBF7]" data-testid="tell-waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light">Tell the waitlist</DialogTitle>
          <DialogDescription className="text-xs" data-testid="tell-waitlist-count">
            {count === null ? "Counting…" : `${count} ${count === 1 ? "person is" : "people are"} waiting. Each gets their own email with the painting's photo and link.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block"><span className="field-label mb-1">Subject</span><input className="admin-input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="tell-waitlist-subject" /></label>
          <label className="block"><span className="field-label mb-1">Message</span><textarea rows={9} className="admin-input" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="tell-waitlist-message" /></label>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-outline px-6 py-3" onClick={onClose} data-testid="tell-waitlist-later">Not now</button>
            <button type="button" className="btn-primary px-6 py-3" disabled={busy || !count || !f.subject.trim() || !f.message.trim()} onClick={send} data-testid="tell-waitlist-send">{busy ? "Sending…" : `Send to ${count ?? "…"}`}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
